import { Component, computed, DestroyRef, inject, input, OnInit, signal, TemplateRef } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import { CommonModule as AppCommonModule } from '@modules/common/common.module';
import { NavbarAiUsageRingComponent } from '@modules/common/components/navbar-ai-usage-ring/navbar-ai-usage-ring.component';
import { RailSkeletonComponent } from '@modules/common/components/rail-skeleton/rail-skeleton.component';
import { NotificationModule } from '@modules/notification/notification.module';
import { UtilsModule } from '@modules/utils/utils.module';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { SpaceStateService } from '@modules/common/services/space-state.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { pendingAfterDelay } from '@modules/common/components/rail-skeleton/rail-skeleton.component';
import { Application } from '@modules/common/types/application';
import CloseToken from '@modules/modal/types/modal-close.injection-token';
import { NotificationsService } from '@modules/notification/services/notifications.service';
import { SPACES_PATH } from '@modules/common/utils/space-route';
import { AppRoutesMap } from '@modules/pages/types/app-routes.map';
import { notesWorkspacePath } from '@modules/notes/routing/notes-workspace-route';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { Space } from '@modules/settings/types/space';
import { Account } from '@frontendlib/common/types/account';
import { AccountService } from '@frontendlib/common/services/account.service';
import {
  type AccountAvatar,
  activeAccountAvatar,
  switcherTitle,
} from '@modules/common/components/spaces-context-menu/account-avatar';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-base-nav-bar',
  templateUrl: './base-nav-bar.component.html',
  styleUrls: ['./base-nav-bar.component.less'],
  providers: [{ provide: CloseToken, useFactory: () => new Subject<void>() }],
  imports: [
    AppCommonModule,
    IconComponent,
    MatIconModule,
    NavbarAiUsageRingComponent,
    NgTemplateOutlet,
    NotificationModule,
    PopoverDirective,
    RailSkeletonComponent,
    UtilsModule,
  ],
})
export class BaseNavBarComponent implements OnInit {
  public readonly navBarContent = input<TemplateRef<unknown>>();
  /** The AI usage control: the ring (Admin, spaces) or the rail header's chart glyph (Settings, Johanna 2026-10-21). */
  public readonly aiUsageAppearance = input<'ring' | 'glyph'>('ring');

  private readonly advancedSearchService = inject(AdvancedSearchService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly notificationsService = inject(NotificationsService);
  private readonly spaceStateService = inject(SpaceStateService);
  private readonly spaceContextStore = inject(SpaceContextStore);
  private readonly router = inject(Router);
  private readonly accountService = inject(AccountService);

  protected readonly closeToken = inject(CloseToken, { self: true });

  public notificationsCount = 0;
  public appContextMenuOpened = false;
  public spacesContextMenuOpened = false;
  /** `undefined` until the space list answers, `null` once it has found none — both kept (#1302 PM-2). */
  public space: Space | null | undefined;
  public account: Account;

  /**
   * What the switcher names, or null while it may not name anything (#1302 PM-2) — never the person's
   * own name inside a team, which is what it fell back to while the team's space was on the wire.
   *
   * A SIGNAL, although the bar's other state is plain fields: the template reading it is what marks
   * the bar for refresh when a name arrives with nothing else changing on screen.
   */
  protected readonly workspaceSwitcherTitle = signal<string | null>(null);

  /** #1180's blocks in the switcher's box, once the name has been missing for `RAIL_SKELETON_DELAY_MS`. */
  protected readonly switcherSkeleton = pendingAfterDelay(computed(() => this.workspaceSwitcherTitle() === null));
  public app: Application = Application.mail;

  ngOnInit(): void {
    this.setApp(this.router.url);

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: NavigationEnd) => {
        this.setApp(event.url);
      });

    this.notificationsService
      .getCount()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count) => (this.notificationsCount = count));

    this.spaceStateService
      .getCurrentSpace()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((space) => {
        this.space = space;
        this.refreshSwitcherTitle();
      });

    this.accountService
      .getAccount(true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((account) => {
        this.account = account;
        this.refreshSwitcherTitle();
      });
  }

  handleAppChange(app: Application) {
    this.advancedSearchService.resetState();
    const route = AppRoutesMap.find((item) => item.app === app).path;
    const destination = app === Application.notes
      ? notesWorkspacePath(this.spaceContextStore.insideTeam() ? this.spaceContextStore.contextId() : undefined)
      : route;
    this.router.navigate([destination]);
  }

  /** The account you are in, drawn exactly as the top of its menu draws it (#1301). */
  protected get switcherAvatar(): AccountAvatar {
    return activeAccountAvatar(this.account, this.space, this.workspaceSwitcherTitle() ?? undefined);
  }

  private refreshSwitcherTitle(): void {
    this.workspaceSwitcherTitle.set(switcherTitle(this.account, this.space, this.spaceContextStore.insideTeam()));
  }

  /**
   * Which app the address is in.
   *
   * `AppRoutesMap` matches on a leading path, and chat's address is no longer one: it is
   * `/spaces/:uuid/chat`, so `startsWith('/chat')` is false and the selector would keep naming
   * whichever app you came from. Every `/spaces…` address belongs to chat — a bare one only exists
   * long enough to resolve into the account's own chat.
   */
  private setApp(url: string) {
    if (/^\/spaces\/[^/]+\/notes(?:[/?#;]|$)/.test(url)) {
      this.app = Application.notes;
      return;
    }
    if (url.startsWith(`/${SPACES_PATH}`)) {
      this.app = Application.chat;
      return;
    }
    this.app = AppRoutesMap.find((item) => url.startsWith(item.path))?.app ?? this.app;
  }
}
